import MUICheckbox from '@mui/material/Checkbox'
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel'
import React from 'react'

import { boxShadowFloat } from '@/_vars'
import { IProps as IconProps, InlineSvgIcon } from '@/atoms/SvgIcon/SvgIcon'
import { E2E, passTestId } from '@/lib/e2e-utils'
import { eventStopper } from '@/lib/utils'
import { mainBlack } from '@/refactor/colors'
import styled, { css, muiUtils } from '@/styles'
import { DefaultProps } from '@/travelsuit'

import CheckboxIntermediateState from './form/checkbox-intermediate-state.svg?raw'
import CheckboxOffDisabled from './form/checkbox-off-disabled.svg?raw'
import CheckboxOff from './form/checkbox-off.svg?raw'
import CheckboxOnDisabled from './form/checkbox-on-disabled.svg?raw'
import CheckboxOn from './form/checkbox-on.svg?raw'

export type CheckboxProps = Omit<
	DefaultProps & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	'onToggle' | 'label' | 'ref'
> &
	E2E & {
		checked?: boolean
		defaultChecked?: boolean
		label?: React.ReactNode
		disabled?: boolean
		controlSize?: IconProps['size']
		onToggle?(value: boolean, e: React.ChangeEvent<HTMLInputElement>): void
		intermediateState?: boolean
		labelProps?: Omit<FormControlLabelProps, 'control' | 'label' | 'variant'>
		ref?: React.Ref<HTMLInputElement>
		checkboxSx?: React.ComponentProps<typeof MUICheckbox>['sx']
	}

const StyledFormControlLabel = styled(FormControlLabel)<{ variant?: string }>`
	&.MuiFormControlLabel-root {
		color: ${mainBlack};
		align-items: start;
		margin-left: -5px;
	}

	&.MuiIconButton-colorPrimary,
	&.MuiIconButton-colorPrimary.Mui-checked {
		&:hover {
			background-color: transparent;
		}
	}

	${({ variant: $variant }) =>
		$variant === 'normal' &&
		css`
			&:hover:not(.Mui-disabled) {
				&.MuiButtonBase-root {
					svg {
						box-shadow: ${boxShadowFloat};
					}
				}
			}
		`}

	${muiUtils.mobile} {
		&:hover:not(.Mui-disabled) {
			&.MuiButtonBase-root {
				svg {
					box-shadow: none;
				}
			}
		}
	}
`

const getOffIcon = (props: CheckboxProps) => {
	if (props.disabled) {
		return CheckboxOffDisabled
	} else {
		return CheckboxOff
	}
}

const getOnIcon = (props: CheckboxProps) => {
	if (props.disabled) {
		return CheckboxOnDisabled
	} else {
		return CheckboxOn
	}
}

export default function Checkbox(props: CheckboxProps) {
	const {
		className,
		checked,
		label,
		name,
		onToggle,
		disabled,
		defaultChecked,
		controlSize,
		intermediateState,
		required,
		value,
		ref,
		checkboxSx,
	} = props

	const iconProps = {
		height: controlSize ?? 20,
		size: controlSize ?? 16,
	}

	return (
		<StyledFormControlLabel
			{...props.labelProps}
			className={className}
			onClick={eventStopper.stopPropagation()}
			label={label}
			data-test={passTestId(props)}
			required={required}
			control={
				<MUICheckbox
					sx={{ paddingX: 1, paddingY: 0, ...checkboxSx }}
					inputProps={{ 'data-test': 'Checkbox' }}
					inputRef={ref}
					icon={<InlineSvgIcon {...iconProps} src={getOffIcon(props)} />}
					checkedIcon={<InlineSvgIcon {...iconProps} src={getOnIcon(props)} />}
					indeterminate={intermediateState}
					indeterminateIcon={<InlineSvgIcon {...iconProps} src={CheckboxIntermediateState} />}
					disableRipple={true}
					checked={checked}
					name={name}
					value={value}
					defaultChecked={defaultChecked}
					disabled={disabled}
					onClick={eventStopper.stopPropagation()}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggle?.(e.target.checked, e)}
					color="primary"
				/>
			}
		/>
	)
}
